import { FunctionComponent } from 'react'
import { Grid } from '@mui/material'
import { AnalyticMetric } from './analytic-metric'
import { IIntensityRatioResult } from '../../../../shared/services/analytics/analytics.service'

/**
 * Renders a single intensity ratio as a standard dashboard metric card (AnalyticMetric),
 * resolving its label/unit/value and the period-over-period comparison, and showing N/A
 * when the denominator (revenue, employees or floor area) is missing for the period. Shared
 * by the featured headline card and the IntensityRatioCards section so they render identically.
 */
export const IntensityRatioMetric: FunctionComponent<{
  ratio: IIntensityRatioResult,
  previousRatios?: IIntensityRatioResult[],
  comparisonLabel?: string,
}> = ({ ratio, previousRatios, comparisonLabel }) => {
  const title = ratio.label ?? ratio.intensityRatio;
  const scopeText = ratio.includesScopeThree
    ? 'All greenhouse gas emissions (Scope 1, 2 and 3)'
    : 'Scope 1 and 2 greenhouse gas emissions';
  const denominatorText = ratio.denominatorType === 'revenue'
    ? 'per £ million of revenue'
    : ratio.denominatorType === 'floorArea'
      ? 'per m² of occupied floor area'
      : 'per employee';
  const denominatorSource = ratio.denominatorType === 'floorArea'
    ? 'Floor area is taken from the occupied floor area of your active locations.'
    : 'Revenue and employee figures are set on each reporting period.';
  const tooltip = `${scopeText} ${denominatorText} for the selected reporting period. ${denominatorSource}`;

  if (!ratio.denominatorAvailable) {
    const message = ratio.denominatorType === 'revenue'
      ? 'No revenue recorded for this period'
      : ratio.denominatorType === 'floorArea'
        ? 'No occupied floor area recorded for your locations'
        : 'No employee numbers recorded for this period';
    return <AnalyticMetric title={title} value={0} tooltip={tooltip} unavailable unavailableMessage={message} />;
  }

  const value = ratio.emissions?.global ?? 0;
  const prev = previousRatios?.find((p) => p.intensityRatio === ratio.intensityRatio);
  const prevValue = prev?.emissions?.global;
  const comparison = (prevValue != null && prev?.denominatorAvailable)
    ? value - prevValue
    : undefined;

  return (
    <AnalyticMetric
      title={title}
      value={value}
      units={ratio.unitLabel}
      tooltip={tooltip}
      comparison={comparison}
      comparisonLabel={comparisonLabel}
    />
  );
};

/**
 * Renders the given intensity ratios (from the settings catalog) as standard dashboard
 * metric cards, one responsive Grid item each so they flow and wrap alongside the other
 * dashboard widgets. MUST be rendered as a child of a <Grid container>. Purely
 * presentational — the parent fetches the data — so it is reusable across the analytics
 * overview, dashboard, and report displays.
 */
export const IntensityRatioCards: FunctionComponent<{
  ratios: IIntensityRatioResult[],
  previousRatios?: IIntensityRatioResult[],
  comparisonLabel?: string,
}> = ({ ratios, previousRatios, comparisonLabel }) => {
  if (!ratios?.length) return null;

  return (
    <>
      {ratios.map((ratio) => (
        <Grid item key={ratio.intensityRatio} xl={4} lg={4} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
          <IntensityRatioMetric ratio={ratio} previousRatios={previousRatios} comparisonLabel={comparisonLabel} />
        </Grid>
      ))}
    </>
  );
};
